// NETWORK ZONE — who this organisation works with (BUILD-BRIEF-R5 §6.2). Self-loading, like
// DossierZone: `usePoll` at 60s, `id="network"`. Placed on OrganisationPage between the Open
// commitments zone and ProfileZone — the people, the tree, the deals and the commitments, then
// WHO this organisation works with, then the cells.
//
// Three sheets. A: the payer mix — who pays for the subject, latest year, with a computed
// "attributed" foot that flags a distribution that does not sum to 100. B: Names — the links
// FROM this organisation, one line per (role, counterpart), quantified rows riding beneath as
// sub-rows. C: Named by — the links TO this organisation (a payer's own page), with a computed,
// unmarked beneficiaries figure. Nothing here invents a value: a mix absent from the record is
// simply not drawn, a beneficiaries figure absent because the subject holds no
// `clients_per_year` is blank, never zero.
import { useCallback, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import {
  Att, Bar, Ev, Fig, Lnk, Ref, Sep, Sheet, Tag, Tbl, Zone, ZoneHead,
} from './PageKit.js';
import ConfirmModal from './ConfirmModal.js';
import { EvidenceSrc } from './ProfileZone.js';
import { usePoll } from './usePoll.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open as openRoute } from '../routes.js';
import {
  declareLink, endLink, getOrganisationLinks, retractLink, supersedeLink,
} from '../api/network.js';
import { listOrganisations } from '../api/organisations.js';
import {
  LINK_BASES, LINK_RESIDUALS, LINK_ROLES, REGIME_TOKENS, SUMMABLE_BASES, linkLabel, quantityText,
  type LinkBasis, type LinkResidual, type LinkRole, type LinkRow, type NamedByRow, type NetLang, type OrganisationLinks,
  type RegimeToken,
} from '../../shared/network.v1.js';
import './NetworkZone.de.js';
import './NetworkZone.fr.js';

export interface NetworkZoneProps {
  organisationId: number;
  /** Embedded on the Network page's focus zone — same component; the route out is the
   *  organisation itself rather than the graph. */
  embedded?: boolean;
  /** Replaces the zone's label (the Network page says "Focus"). */
  heading?: ReactNode;
  /** A note beside the label, before the held-by-rule / proposed tally. */
  note?: ReactNode;
  /** When given, a "Clear focus" verb sits beside "Add link". */
  onClear?: () => void;
  /** Rendered inside the zone after the sheets (the Network page's neighbourhood graph). */
  after?: ReactNode;
}

const POLL_MS = 60_000;

/** Sheet B's role order (§6.2) — not `LINK_ROLES`' declaration order. */
const SHEET_B_ROLE_ORDER: LinkRole[] = [
  'payer', 'funder', 'referrer', 'accreditor', 'umbrella', 'partner', 'vendor', 'consultant', 'auditor',
];

/** The one identity a link's counterpart can be: an organisation, a regime alone, or a residual —
 *  grouping rows into "one line per (role, counterpart)" needs this key, not the row's own id. */
function counterpartKey(row: Pick<LinkRow, 'counterpart_id' | 'regime' | 'residual' | 'label'>): string {
  if (row.counterpart_id != null) return `org:${row.counterpart_id}`;
  if (row.regime) return `regime:${row.regime}`;
  if (row.residual) return `residual:${row.residual}`;
  return `label:${row.label ?? ''}`;
}

function periodNode(row: LinkRow, fmtDate: (iso: string | null | undefined) => string) {
  if (row.year && row.ended_on) {
    const endYear = /^\d{4}/.test(row.ended_on) ? row.ended_on.slice(0, 4) : row.ended_on;
    return <Fig>{row.year} – {endYear}</Fig>;
  }
  if (row.year) return <Fig>{row.year}</Fig>;
  if (row.as_of) return <Fig>{fmtDate(row.as_of)}</Fig>;
  return null;
}

function ClaimCell({ row, t }: { row: LinkRow; t: (k: string, v?: Record<string, string | number>) => string }) {
  return (
    <>
      <Att kind="held" />
      {row.confirmed_by === 0 && <> <Tag tone="ghost">{t('held by rule')}</Tag></>}
      {row.method === 'estimated' && <> <Tag tone="ghost">{t('estimated')}</Tag></>}
    </>
  );
}

function EvidenceCell({
  row, open, onToggle, t, fmtDate,
}: {
  row: LinkRow;
  open: boolean;
  onToggle: () => void;
  t: (k: string) => string;
  fmtDate: (iso: string | null | undefined) => string;
}) {
  if (!row.quote && !row.evidence_ref) return null;
  return (
    <>
      <Lnk onClick={onToggle}>{open ? t('hide evidence') : t('Show')}</Lnk>
      {open && (
        <Ev src={<EvidenceSrc evidenceKind={row.evidence_kind} evidenceRef={row.evidence_ref} fmtDate={fmtDate} asOf={row.as_of} />}>
          {row.quote ?? t('The evidence names a source but keeps no verbatim passage.')}
        </Ev>
      )}
    </>
  );
}

// ── Sheet A — the payer mix ──────────────────────────────────────────────────

function PayerMixSheet({ data, lang, t, fmtDate, openEvidence, toggleEvidence }: {
  data: OrganisationLinks;
  lang: NetLang;
  t: (k: string, v?: Record<string, string | number>) => string;
  fmtDate: (iso: string | null | undefined) => string;
  openEvidence: Set<string>;
  toggleEvidence: (key: string) => void;
}) {
  const mix = data.mix;
  if (!mix) return null;
  const attributed = mix.attributed_pct;
  const offBy = attributed === null ? 0 : Math.abs(attributed - 100);
  // Largest first — "if the biggest category is the most important, think about putting that
  // first and ordering the rest of the categories in decreasing numerical order" (Knaflic p. 58).
  const rows = [...mix.rows].sort((a, b) => (b.share_computed ?? b.quantity) - (a.share_computed ?? a.quantity));
  const isPct = mix.basis === 'participants_pct' || mix.basis === 'revenue_pct';
  const maxAmount = rows.reduce((m, r) => Math.max(m, r.quantity), 0);
  return (
    <>
      <div className="zone-head" style={{ marginTop: 12 }}>
        <span className="u-label">{t('Payer mix')}</span>
        {mix.year != null && <span className="zone-note"><Fig>{mix.year}</Fig></span>}
        <span className="zone-note">{linkLabel(lang, 'basis', mix.basis)} · {t('largest first; a bar is a length on a zero baseline, the dotted rest unattributed')}</span>
      </div>
      <Sheet clip>
        <Tbl>
          <thead>
            <tr>
              <th>{t('Counterpart')}</th>
              <th className="num">{t('Share')}</th>
              <th>{t('Length')}</th>
              <th>{t('Claim')}</th>
              <th>{t('Evidence')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = `mix:${row.link_id}`;
              const isNotFound = row.method === 'not_found';
              const length = isNotFound ? null : (row.share_computed ?? row.quantity);
              const lengthMax = isPct || row.share_computed != null ? 100 : maxAmount;
              const label = row.counterpart_name
                ? <Lnk onClick={() => openRoute(ROUTES.org({ id: row.counterpart_id! }))}><Ref>{row.counterpart_ref}</Ref> {row.counterpart_name}</Lnk>
                : row.regime
                  ? <>{linkLabel(lang, 'regime', row.regime)} <span className="fd">({t('regime')})</span></>
                  : row.residual
                    ? <span className="voice-record">{linkLabel(lang, 'residual', row.residual)}</span>
                    : <span className="voice-record">—</span>;
              return (
                <tr key={row.link_id}>
                  <td>{label}</td>
                  <td className="num">
                    {isNotFound ? (
                      <span className="fv--nil">{t('not found')} · {fmtDate(row.as_of)}</span>
                    ) : (
                      <>
                        {mix.basis && <Fig>{quantityText(lang, mix.basis, row.quantity, row.qualifier)}</Fig>}
                        {row.share_computed != null && (
                          <> <span className="fd" title={row.share_title ?? undefined}>{row.share_computed} % {t('of revenue')}</span></>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    {length != null && (
                      <Bar
                        value={length}
                        max={lengthMax}
                        rest={isPct || row.share_computed != null ? 'unknown' : 'none'}
                        fill={row.method === 'estimated' ? 'estimated' : row.share_computed != null ? 'computed' : 'held'}
                        title={row.share_title ?? (mix.basis ? quantityText(lang, mix.basis, row.quantity, row.qualifier) : undefined)}
                      />
                    )}
                  </td>
                  <td>
                    {!isNotFound && (
                      <>
                        <Att kind="held" />
                        {row.held_by_rule && <> <Tag tone="ghost">{t('held by rule')}</Tag></>}
                        {row.method === 'estimated' && <> <Tag tone="ghost">{t('estimated')}</Tag></>}
                      </>
                    )}
                  </td>
                  <td>
                    {(row.quote || row.evidence_ref) && (
                      <>
                        <Lnk onClick={() => toggleEvidence(key)}>{openEvidence.has(key) ? t('hide evidence') : t('Show')}</Lnk>
                        {openEvidence.has(key) && (
                          <Ev src={<EvidenceSrc evidenceKind={row.evidence_kind} evidenceRef={row.evidence_ref} fmtDate={fmtDate} asOf={row.as_of} />}>
                            {row.quote ?? ''}
                          </Ev>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="sheet-foot">
              <td>{t('attributed')}</td>
              <td className="num">
                {attributed === null
                  ? <span className="fv--unknown">{t('not collected')}</span>
                  : <Fig className={offBy > 2 ? 'fv--attend' : undefined}>{attributed} %</Fig>}
              </td>
              <td>
                {attributed !== null && (
                  <Bar
                    value={Math.min(attributed, 100)}
                    max={100}
                    rest="unknown"
                    fill="computed"
                    title={t('Σ of the shares above — computed, carries no mark')}
                    label={attributed < 100 ? t('{r} % unattributed', { r: 100 - attributed }) : undefined}
                  />
                )}
              </td>
              <td colSpan={2} />
            </tr>
          </tbody>
        </Tbl>
      </Sheet>
    </>
  );
}

// ── Sheet B — Names ──────────────────────────────────────────────────────────

function NamesSheet({
  items, proposed, lang, t, fmtDate, openEvidence, toggleEvidence, onAction,
}: {
  items: LinkRow[];
  proposed: OrganisationLinks['proposed'];
  lang: NetLang;
  t: (k: string, v?: Record<string, string | number>) => string;
  fmtDate: (iso: string | null | undefined) => string;
  openEvidence: Set<string>;
  toggleEvidence: (key: string) => void;
  onAction: (row: LinkRow, action: 'correct' | 'end' | 'retract') => void;
}) {
  const byRole = useMemo(() => {
    const out = new Map<LinkRole, Map<string, LinkRow[]>>();
    for (const role of SHEET_B_ROLE_ORDER) out.set(role, new Map());
    for (const row of items) {
      const roleMap = out.get(row.role);
      if (!roleMap) continue; // a role Sheet B does not section by (there is none today)
      const key = counterpartKey(row);
      const list = roleMap.get(key) ?? [];
      list.push(row);
      roleMap.set(key, list);
    }
    return out;
  }, [items]);

  const anyRows = items.length > 0 || proposed.length > 0;
  if (!anyRows) return null;

  return (
    <>
      <div className="zone-head" style={{ marginTop: 12 }}>
        <span className="u-label">{t('Names')}</span>
      </div>
      <Sheet clip>
        <Tbl>
          <thead>
            <tr>
              <th>{t('Counterpart')}</th>
              <th>{t('Role')}</th>
              <th className="num">{t('Quantity')}</th>
              <th className="num">{t('Period')}</th>
              <th>{t('Claim')}</th>
              <th>{t('Evidence')}</th>
            </tr>
          </thead>
          <tbody>
            {SHEET_B_ROLE_ORDER.flatMap((role) => {
              const groups = byRole.get(role);
              if (!groups || groups.size === 0) return [];
              const rows: ReactElement[] = [
                <tr key={`h:${role}`} className="fgroup-h">
                  <td colSpan={6} className="u-label">
                    {linkLabel(lang, 'role', role)} <Fig>{groups.size}</Fig>
                  </td>
                </tr>,
              ];
              for (const [key, group] of groups) {
                const anchor = group.find((r) => r.basis === null) ?? group[0];
                const figureRows = group.filter((r) => r !== anchor);
                const counterpartLabel = anchor.counterpart_name
                  ? <Lnk onClick={() => openRoute(ROUTES.org({ id: anchor.counterpart_id! }))}><Ref>{anchor.counterpart_ref}</Ref> {anchor.counterpart_name}</Lnk>
                  : anchor.regime
                    ? <>{linkLabel(lang, 'regime', anchor.regime)} <Tag tone="ghost">{t('regime, no body named')}</Tag></>
                    : anchor.residual
                      ? <span className="voice-record">{linkLabel(lang, 'residual', anchor.residual)}</span>
                      : <span className="voice-record">—</span>;
                const evKey = `b:${anchor.id}`;
                rows.push(
                  <tr key={key}>
                    <td>{counterpartLabel}</td>
                    <td>
                      {linkLabel(lang, 'role', anchor.role)}
                      {anchor.counterpart_id != null && anchor.regime && <> <span className="fd">· {linkLabel(lang, 'regime', anchor.regime)}</span></>}
                    </td>
                    <td className="num">{anchor.basis && anchor.quantity != null ? <Fig>{quantityText(lang, anchor.basis, anchor.quantity, anchor.qualifier)}</Fig> : null}</td>
                    <td className="num">{periodNode(anchor, fmtDate)}</td>
                    <td><ClaimCell row={anchor} t={t} /></td>
                    <td>
                      <EvidenceCell row={anchor} open={openEvidence.has(evKey)} onToggle={() => toggleEvidence(evKey)} t={t} fmtDate={fmtDate} />
                      {' '}
                      <RowMenu row={anchor} t={t} onAction={onAction} />
                    </td>
                  </tr>,
                );
                for (const fig of figureRows) {
                  const figKey = `b:${fig.id}`;
                  rows.push(
                    <tr key={figKey} className="row--sub">
                      <td />
                      <td />
                      <td className="num">{fig.basis && fig.quantity != null ? <Fig>{quantityText(lang, fig.basis, fig.quantity, fig.qualifier)}</Fig> : null}{fig.label && <> <span className="fd">· {fig.label}</span></>}</td>
                      <td className="num">{periodNode(fig, fmtDate)}</td>
                      <td><ClaimCell row={fig} t={t} /></td>
                      <td>
                        <EvidenceCell row={fig} open={openEvidence.has(figKey)} onToggle={() => toggleEvidence(figKey)} t={t} fmtDate={fmtDate} />
                        {' '}
                        <RowMenu row={fig} t={t} onAction={onAction} />
                      </td>
                    </tr>,
                  );
                }
              }
              return rows;
            })}
            {proposed.map((g) => (
              <tr key={`ghost:${g.proposal_id}`} className="ghost">
                <td>
                  <Att kind="proposed" />{' '}
                  {g.counterpart_name
                    ? <>{g.counterpart_ref ? <Ref>{g.counterpart_ref}</Ref> : null} {g.counterpart_name}</>
                    : g.regime
                      ? linkLabel(lang, 'regime', g.regime)
                      : g.residual
                        ? linkLabel(lang, 'residual', g.residual)
                        : '—'}
                </td>
                <td>{linkLabel(lang, 'role', g.role)}</td>
                <td className="num">{g.basis && g.quantity != null ? <Fig>{quantityText(lang, g.basis, g.quantity, g.qualifier)}</Fig> : null}</td>
                <td className="num">{g.year != null ? <Fig>{g.year}</Fig> : null}</td>
                <td />
                <td><Lnk onClick={() => openRoute(ROUTES.review({ proposal: g.proposal_id }))}>{t('In Review')}</Lnk></td>
              </tr>
            ))}
          </tbody>
        </Tbl>
      </Sheet>
    </>
  );
}

function RowMenu({ row, t, onAction }: {
  row: LinkRow;
  t: (k: string) => string;
  onAction: (row: LinkRow, action: 'correct' | 'end' | 'retract') => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative' }}>
      <Lnk onClick={() => setOpen((v) => !v)} title={t('Correct figure')}>⋯</Lnk>
      {open && (
        <span className="chips" style={{ display: 'inline-flex', gap: 6, marginLeft: 6 }}>
          <Lnk onClick={() => { setOpen(false); onAction(row, 'correct'); }}>{t('Correct figure')}</Lnk>
          <Sep />
          <Lnk onClick={() => { setOpen(false); onAction(row, 'end'); }}>{t('Mark ended')}</Lnk>
          <Sep />
          <Lnk onClick={() => { setOpen(false); onAction(row, 'retract'); }}>{t('Retract')}</Lnk>
        </span>
      )}
    </span>
  );
}

// ── Sheet C — Named by ───────────────────────────────────────────────────────

function NamedBySheet({ rows, data, lang, t, fmtDate, fmtNum }: {
  rows: LinkRow[];
  data: OrganisationLinks;
  lang: NetLang;
  t: (k: string, v?: Record<string, string | number>) => string;
  fmtDate: (iso: string | null | undefined) => string;
  fmtNum: (n: number | string | null | undefined) => string;
}) {
  if (rows.length === 0) return null;
  const volume = data.channel_volume ?? null;
  const people = data.people_accounted ?? null;
  const coveragePct = volume && people != null && volume.value > 0 ? Math.round((people / volume.value) * 100) : null;
  const sorted = [...rows].sort((a, b) => (b.quantity ?? -1) - (a.quantity ?? -1));
  const sheetYear = sorted.reduce<number | null>((max, r) => (r.year != null && (max === null || r.year > max) ? r.year : max), null);
  const inYear = sorted.filter((r) => sheetYear === null || r.year === sheetYear);
  const sums: Partial<Record<LinkBasis, number>> = {};
  for (const r of inYear) {
    if (r.basis && r.quantity != null && (SUMMABLE_BASES as readonly string[]).includes(r.basis)) {
      sums[r.basis] = (sums[r.basis] ?? 0) + r.quantity;
    }
  }
  const withCount = sorted.filter((r) => r.quantity != null).length;

  return (
    <>
      <div className="zone-head" style={{ marginTop: 12 }}>
        <span className="u-label">{t('Named by')}</span>
      </div>
      <Sheet clip>
        <Tbl>
          <thead>
            <tr>
              <th>{t('Organisation')}</th>
              <th>{t('Role')}</th>
              <th className="num">{t('Quantity')}</th>
              <th>{t('Length')}</th>
              <th className="num">{t('Beneficiaries')}</th>
              <th className="num">{t('Year')}</th>
              <th>{t('Claim')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const nb = row as NamedByRow;
              return (
                <tr key={row.id}>
                  <td><Lnk onClick={() => openRoute(ROUTES.org({ id: row.subject_id }))}><Ref>{row.subject_ref}</Ref> {row.subject_name}</Lnk></td>
                  <td className="nowrap">{linkLabel(lang, 'reverse', row.role)}</td>
                  <td className="num nowrap">{row.basis && row.quantity != null ? <Fig>{quantityText(lang, row.basis, row.quantity, row.qualifier)}</Fig> : null}</td>
                  <td>
                    {(row.basis === 'participants_pct' || row.basis === 'revenue_pct') && row.quantity != null && (
                      <Bar
                        value={row.quantity}
                        max={100}
                        rest="unknown"
                        fill={row.method === 'estimated' ? 'estimated' : 'held'}
                        title={t('{q} of {who}’s {basis} — the dotted rest goes to its other payers', {
                          q: quantityText(lang, row.basis, row.quantity, row.qualifier), who: row.subject_name ?? '', basis: linkLabel(lang, 'basis', row.basis).toLowerCase(),
                        })}
                      />
                    )}
                  </td>
                  <td className="num">
                    {nb.beneficiaries_computed != null
                      ? <span className="fd" title={nb.beneficiaries_title ?? undefined}>{fmtNum(nb.beneficiaries_computed)}</span>
                      : null}
                  </td>
                  <td className="num">{row.year != null ? <Fig>{row.year}</Fig> : null}</td>
                  <td><ClaimCell row={row} t={t} /></td>
                </tr>
              );
            })}
            <tr className="sheet-foot">
              <td colSpan={7}>
                {t('{n} of {m} organisations give a count', { n: withCount, m: sorted.length })}
                {(sums.participants_count || sums.places_count || sums.amount_chf) && sheetYear != null && (
                  <>
                    {' '}<Sep />{' '}
                    {sums.participants_count ? <>Σ <Fig>{fmtNum(sums.participants_count)}</Fig> {t('participants')} </> : null}
                    {sums.places_count ? <>Σ <Fig>{fmtNum(sums.places_count)}</Fig> {t('places')} </> : null}
                    {sums.amount_chf ? <>Σ <Fig>CHF {fmtNum(sums.amount_chf)}</Fig> </> : null}
                    (<Fig>{sheetYear}</Fig>)
                  </>
                )}
              </td>
            </tr>
            <tr className="sheet-foot">
              <td colSpan={7}>
                {/* The coverage line (BUILD-BRIEF-R5 §1.7): the people the named providers account for
                    against this body's own channel volume. Computed, no mark; the rest is unknown,
                    never zero — and without a volume there is no denominator, so no bar. */}
                {volume ? (
                  <>
                    {people != null
                      ? t('known providers account for {n} of the channel’s {m} people in a measure ({when}); the rest is not accounted for', {
                        n: fmtNum(people), m: fmtNum(volume.value), when: volume.year != null ? String(volume.year) : fmtDate(volume.as_of),
                      })
                      : t('the channel counts {m} people in a measure ({when}); no provider gives a count yet', {
                        m: fmtNum(volume.value), when: volume.year != null ? String(volume.year) : fmtDate(volume.as_of),
                      })}
                    {volume.method === 'estimated' && <> <Tag tone="ghost">{t('estimated')}</Tag></>}
                    {people != null && coveragePct != null && (
                      <>
                        {' '}
                        <Bar
                          className="bar--narrow"
                          value={people}
                          max={volume.value}
                          rest="unknown"
                          fill="computed"
                          title={data.people_title ?? undefined}
                          label={<>{coveragePct} %</>}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <span className="fv--unknown">{t('channel volume not collected — the coverage has no denominator until a run reads channel.measure_participants')}</span>
                )}
              </td>
            </tr>
          </tbody>
        </Tbl>
      </Sheet>
    </>
  );
}

// ── the declare-link form ────────────────────────────────────────────────────

type CounterpartMode = 'organisation' | 'regime' | 'residual';

function DeclareLinkForm({ organisationId, t, onDone, onCancel }: {
  organisationId: number;
  t: (k: string, v?: Record<string, string | number>) => string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<CounterpartMode>('organisation');
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<Array<{ id: number; name: string; ref: string | null }>>([]);
  const [counterpartId, setCounterpartId] = useState<number | null>(null);
  const [regime, setRegime] = useState<RegimeToken>('ri');
  const [residual, setResidual] = useState<LinkResidual>('other_mandates');
  const [role, setRole] = useState<LinkRole>('payer');
  const [basis, setBasis] = useState<LinkBasis | ''>('');
  const [quantity, setQuantity] = useState('');
  const [year, setYear] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback((q: string) => {
    setQuery(q);
    setCounterpartId(null);
    if (q.trim().length < 2) { setMatches([]); return; }
    listOrganisations({ q: q.trim() }).then((r) => {
      setMatches(r.items.slice(0, 8).map((o) => ({ id: o.id, name: o.name, ref: o.ref })));
    }).catch(() => setMatches([]));
  }, []);

  const submit = async () => {
    if (mode === 'organisation' && counterpartId == null) { setError(t('Pick an organisation from the list.')); return; }
    setSaving(true);
    setError(null);
    try {
      await declareLink({
        subject_id: organisationId,
        counterpart_id: mode === 'organisation' ? counterpartId! : undefined,
        regime: mode === 'regime' ? regime : undefined,
        residual: mode === 'residual' ? residual : undefined,
        role,
        basis: basis || undefined,
        quantity: basis ? quantity || undefined : undefined,
        year: year ? Number(year) : undefined,
        note: note || undefined,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="row--new" style={{ padding: 12 }}>
      <div className="filter-chips" style={{ marginBottom: 8 }}>
        {(['organisation', 'regime', 'residual'] as CounterpartMode[]).map((m) => (
          <button key={m} type="button" className={`chip${mode === m ? ' active' : ''}`} onClick={() => setMode(m)}>
            {m === 'organisation' ? t('Organisation') : m === 'regime' ? t('Regime') : t('Residual')}
          </button>
        ))}
      </div>
      {mode === 'organisation' && (
        <div className="form-group">
          <label className="form-label" htmlFor="net-cp-search">{t('Counterpart')}</label>
          <input
            id="net-cp-search"
            className="form-input"
            value={query}
            onChange={(e) => search(e.target.value)}
            placeholder={t('Name, ref…')}
          />
          {matches.length > 0 && (
            <div className="chips" style={{ marginTop: 4 }}>
              {matches.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`chip${counterpartId === m.id ? ' active' : ''}`}
                  onClick={() => { setCounterpartId(m.id); setQuery(m.name); setMatches([]); }}
                >
                  {m.ref ? <Ref>{m.ref}</Ref> : null} {m.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {mode === 'regime' && (
        <div className="form-group">
          <label className="form-label" htmlFor="net-cp-regime">{t('Regime')}</label>
          <select id="net-cp-regime" className="form-input" value={regime} onChange={(e) => setRegime(e.target.value as RegimeToken)}>
            {REGIME_TOKENS.map((r) => <option key={r} value={r}>{linkLabel('en', 'regime', r)}</option>)}
          </select>
        </div>
      )}
      {mode === 'residual' && (
        <div className="form-group">
          <label className="form-label" htmlFor="net-cp-residual">{t('Residual')}</label>
          <select id="net-cp-residual" className="form-input" value={residual} onChange={(e) => setResidual(e.target.value as LinkResidual)}>
            {LINK_RESIDUALS.map((r) => <option key={r} value={r}>{linkLabel('en', 'residual', r)}</option>)}
          </select>
        </div>
      )}
      <div className="form-group">
        <label className="form-label" htmlFor="net-role">{t('Role')}</label>
        <select id="net-role" className="form-input" value={role} onChange={(e) => setRole(e.target.value as LinkRole)}>
          {LINK_ROLES.map((r) => <option key={r} value={r}>{linkLabel('en', 'role', r)}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="net-basis">{t('Basis')}</label>
        <select id="net-basis" className="form-input" value={basis} onChange={(e) => setBasis(e.target.value as LinkBasis | '')}>
          <option value="">{t('None')}</option>
          {LINK_BASES.map((b) => <option key={b} value={b}>{linkLabel('en', 'basis', b)}</option>)}
        </select>
      </div>
      {basis && (
        <div className="form-group">
          <label className="form-label" htmlFor="net-quantity">{t('Quantity')}</label>
          <input id="net-quantity" className="form-input" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </div>
      )}
      <div className="form-group">
        <label className="form-label" htmlFor="net-year">{t('Year')}</label>
        <input id="net-year" className="form-input" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026" />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="net-note">{t('Note')}</label>
        <textarea id="net-note" className="form-input" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {error && <p className="zone-empty">{error}</p>}
      <div className="chips" style={{ marginTop: 8 }}>
        <button type="button" className="btn btn-accent" disabled={saving} onClick={() => { void submit(); }}>
          {saving ? t('Saving…') : t('Save')}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>{t('Cancel')}</button>
      </div>
    </div>
  );
}

// ── the zone ──────────────────────────────────────────────────────────────

export default function NetworkZone({ organisationId, embedded, heading, note, onClear, after }: NetworkZoneProps) {
  const t = useT();
  const { lang, fmtDate, fmtNum } = useLocale();

  const fetcher = useCallback(() => getOrganisationLinks(organisationId), [organisationId]);
  const poll = usePoll<OrganisationLinks>(fetcher, POLL_MS);
  const data = poll.data;

  const [declaring, setDeclaring] = useState(false);
  const [openEvidence, setOpenEvidence] = useState<Set<string>>(new Set());
  const toggleEvidence = useCallback((key: string) => {
    setOpenEvidence((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const [modal, setModal] = useState<{ row: LinkRow; action: 'correct' | 'end' | 'retract' } | null>(null);
  const [modalQuantity, setModalQuantity] = useState('');
  const [modalDate, setModalDate] = useState('');
  const [modalNote, setModalNote] = useState('');
  const [modalSaving, setModalSaving] = useState(false);

  const openModal = (row: LinkRow, action: 'correct' | 'end' | 'retract') => {
    setModal({ row, action });
    setModalQuantity(row.quantity != null ? String(row.quantity) : '');
    setModalDate('');
    setModalNote('');
  };

  const confirmModal = async () => {
    if (!modal) return;
    setModalSaving(true);
    try {
      if (modal.action === 'correct') await supersedeLink(modal.row.id, { quantity: modalQuantity, note: modalNote });
      else if (modal.action === 'end') await endLink(modal.row.id, modalDate);
      else await retractLink(modal.row.id, modalNote);
      setModal(null);
      void poll.refresh();
    } finally {
      setModalSaving(false);
    }
  };

  // every held link that touches this organisation — the ones it names AND the ones naming it,
  // so a payer's own zone does not read "0" above a sheet of two providers
  const heldCount = (data?.items.length ?? 0) + (data?.named_by.length ?? 0);
  const heldByRule = data?.items.filter((r) => r.confirmed_by === 0).length ?? 0;
  const proposedCount = data?.proposed.length ?? 0;

  return (
    <Zone id="network">
      <ZoneHead
        label={heading ?? t('Network')}
        count={<Fig>{heldCount}</Fig>}
        note={(note != null || heldByRule > 0 || proposedCount > 0)
          ? (
            <>
              {note}
              {note != null && (heldByRule > 0 || proposedCount > 0) && <> <Sep /> </>}
              {heldByRule > 0 && <>{t('held by rule')} <Fig>{heldByRule}</Fig></>}
              {heldByRule > 0 && proposedCount > 0 && <Sep />}
              {proposedCount > 0 && <>{t('proposed')} <Fig>{proposedCount}</Fig></>}
            </>
          )
          : undefined}
        endNote={(
          <>
            {onClear && <><Lnk onClick={onClear}>{t('Clear focus')}</Lnk> <Sep /> </>}
            <Lnk onClick={() => setDeclaring((v) => !v)}>{t('Add link')}</Lnk>
          </>
        )}
        route={!embedded ? (
          <button type="button" className="zone-route" onClick={() => openRoute(ROUTES.network({ focus: organisationId }))}>
            {t('Open in network')} →
          </button>
        ) : (
          <button type="button" className="zone-route" onClick={() => openRoute(ROUTES.org({ id: organisationId }))}>
            {t('Open organisation')} →
          </button>
        )}
      />

      {declaring && (
        <DeclareLinkForm
          organisationId={organisationId}
          t={t}
          onDone={() => { setDeclaring(false); void poll.refresh(); }}
          onCancel={() => setDeclaring(false)}
        />
      )}

      {poll.error && !data ? (
        <p className="zone-empty">{t('The network could not be read: {error}', { error: poll.error })}</p>
      ) : poll.loading && !data ? (
        <p className="zone-empty">{t('Loading…')}</p>
      ) : !data || (data.items.length === 0 && data.named_by.length === 0 && data.proposed.length === 0 && !data.mix) ? (
        <p className="zone-empty">
          {t('No link is on the record for this organisation. A research run files the payers, mandants and federations its pages name.')}
        </p>
      ) : (
        <>
          <PayerMixSheet data={data} lang={lang as NetLang} t={t} fmtDate={fmtDate} openEvidence={openEvidence} toggleEvidence={toggleEvidence} />
          <NamesSheet
            items={data.items}
            proposed={data.proposed}
            lang={lang as NetLang}
            t={t}
            fmtDate={fmtDate}
            openEvidence={openEvidence}
            toggleEvidence={toggleEvidence}
            onAction={openModal}
          />
          <NamedBySheet rows={data.named_by} data={data} lang={lang as NetLang} t={t} fmtDate={fmtDate} fmtNum={fmtNum} />
        </>
      )}
      {after}

      <ConfirmModal
        open={modal?.action === 'correct'}
        title={t('Correct figure')}
        confirmLabel={modalSaving ? t('Saving…') : t('Save')}
        onConfirm={() => { void confirmModal(); }}
        onCancel={() => setModal(null)}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="net-modal-qty">{t('Quantity')}</label>
          <input id="net-modal-qty" className="form-input" value={modalQuantity} onChange={(e) => setModalQuantity(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="net-modal-note">{t('Note')}</label>
          <textarea id="net-modal-note" className="form-input" value={modalNote} onChange={(e) => setModalNote(e.target.value)} />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={modal?.action === 'end'}
        title={t('Mark ended')}
        confirmLabel={modalSaving ? t('Saving…') : t('Save')}
        onConfirm={() => { void confirmModal(); }}
        onCancel={() => setModal(null)}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="net-modal-date">{t('Ended on')}</label>
          <input id="net-modal-date" type="date" className="form-input" value={modalDate} onChange={(e) => setModalDate(e.target.value)} />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={modal?.action === 'retract'}
        title={t('Retract')}
        danger
        confirmLabel={modalSaving ? t('Saving…') : t('Retract')}
        onConfirm={() => { void confirmModal(); }}
        onCancel={() => setModal(null)}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="net-modal-retract-note">{t('Note')}</label>
          <textarea id="net-modal-retract-note" className="form-input" value={modalNote} onChange={(e) => setModalNote(e.target.value)} />
        </div>
      </ConfirmModal>
    </Zone>
  );
}
